import { DependencyList, useEffect, useRef, useState } from 'react';
import 'whatwg-fetch';

const defaultFetchErrorMessage = bfTranslate('Something went wrong. Try again.');

export type FetchFields =
  'asset_count'|
  'asset_data'|
  'attachment_count'|
  'availability'|
  'availability_end'|
  'availability_start'|
  'background_color'|
  'best_link_for'|
  'best_metadata'|
  'brandfolder_cdn_key'|
  'brandfolder_user_sources'|
  'cdn_url'|
  'collection_user_sources'|
  'combined_size'|
  'content_html'|
  'content_json'|
  'created_at'|
  'download_url'|
  'extracted_colors'|
  'extracted_tag_names'|
  'extracted_text'|
  'extension'|
  'folder_path'|
  'formatted_description'|
  'html'|
  'is_downloadable'|
  'label_names'|
  'metadata'|
  'name'|
  'other_metadata'|
  'parent_id'|
  'position'|
  'section_count'|
  'site_id'|
  'storage'|
  'tag_names'|
  'thumbnail_url'|
  'thumbnailed'|
  'type'|
  'updated_at'|
  'version_count'|
  'view_thumbnail_retina'|
  'values'

export type FetchInclude =
  'asset'|
  'assets'|
  'attachments'|
  'brandfolder'|
  'brandfolders'|
  'collection'|
  'collections'|
  'custom_field_key'|
  'custom_field_values'|
  'custom_fields'|
  'inviteable'|
  'inviter'|
  'labels'|
  'organization'|
  'search_filters'|
  'section'|
  'sections'|
  'subcollections'|
  'tags'

interface FetchJsonOptions {
  url: string;
  /** defaults to true */
  bearerToken?: boolean;
  /** body will be stringified inside fetchJson */
  body?: object;
  /** Error message to return on rejection */
  error?: string;
  /** Adds one or more &fields= params */
  fields?: FetchFields[] | FetchFields;
  /** Defaults to 'Content-Type': 'application/json' */
  headers?: { [key: string]: string };
  /** Adds one or more &include= params */
  include?: FetchInclude[] | FetchInclude;
  /** Defaults to GET */
  method?: 'DELETE' | 'GET' | 'PATCH' | 'PUT' | 'POST';
  /** Allow user defined params */
  params?: { [param: string]: string | boolean | number };
  /** Allows consumer to cancel/abort the fetch call */
  signal?: AbortSignal;
}

/**
 * Abstracted fetch API call for retrieving JSON data. Defaults to GET.
 * @example await fetchJson<MyInterface>({ url: 'https://mywebsite.com/api/stuff' })
 * @param {FetchJsonOptions} options FetchJsonOptions
 * @returns {Promise<T>} Promise<T>
 */
export const fetchJson = async<T extends unknown>(options: FetchJsonOptions): Promise<T> => {
  const { bearerToken = true, error, fields, headers, include, method = 'GET', params, signal, url } = options;
  let { body } = options;

  if (body) {
    body = { ...body, queue_priority: 'high' };
  }

  const requestUrl = new URL(url, window.location.origin);
  if (!body) requestUrl.searchParams.append('queue_priority', 'high');
  if (fields) requestUrl.searchParams.append('fields', Array.isArray(fields) ? fields.join(',') : fields);
  if (include) requestUrl.searchParams.append('include', Array.isArray(include) ? include.join(',') : include);
  if (params) {
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        requestUrl.searchParams.append(key, params[key].toString());
      }
    });
  }

  try {
    const response = await fetch(requestUrl.toString(), {
      body: body ? JSON.stringify(body) : null,
      headers: {
        // eslint-disable-next-line @typescript-eslint/camelcase
        Authorization: bearerToken ? `Bearer ${BF_Token}` : '',
        'Content-Type': 'application/json',
        ...headers
      },
      method,
      signal
    });

    // this needs to be before response.ok, because you can't do response.json() after response.ok
    // (json will be undefined when doing "const json = await response.json()", not sure why)
    const json = response.status !== 204 ? await response.json() : null;
    if (response.ok || (response.status >= 200 && response.status < 300)) {
      return Promise.resolve(json);
    }

    if (response.status === 422) {
      return Promise.reject(json);
    }

    throw Error;
  } catch (err) {
    // error is the optional string passed in above
    return Promise.reject(new Error(error || err?.message || defaultFetchErrorMessage));
  }
};

/**
 * A Promise to delay a Promise
 * @param milliseconds number
 * @returns {Promise<void>} Promise<void>
 */
export const delay = (milliseconds: number): Promise<void> => new Promise((_) => setTimeout(_, milliseconds));

interface FetchMockJsonOptions extends FetchJsonOptions {
  /** Mocked fetch response data */
  data: unknown;
  /** Timeout in milliseconds before resolve/reject */
  delayTimeout?: number;
  /** Mock rejecting the API call */
  reject?: boolean;
}

/**
 * Mock fetch API call for retrieving JSON data.
 * This can be used for stubbing front end screens before API endpoints are created.
 * @example await fetchMockJson<MyInterface>({ data: MyMockData, url: 'https://mywebsite.com/api/stuff' })
 * @param {FetchMockJsonOptions} options FetchMockJsonOptions
 * @returns {Promise<T | unknown>} Promise<T | unknown>
 */
export const fetchMockJson = async<T extends unknown>(options: FetchMockJsonOptions): Promise<T> => {
  const { data, delayTimeout = 1000, error, reject } = options;
  try {
    await delay(delayTimeout);
    if (reject) {
      throw Error;
    }
    return Promise.resolve((data as T));
  } catch (err) {
    return Promise.reject(new Error(error || defaultFetchErrorMessage));
  }
};

export interface FetchWithPollingResponse<T> {
  error: boolean;
  success: boolean;
  timeout: boolean;
  polledData?: T;
}

/**
 * A reusable Promise polling mechanism.
 * By default, this will try polling your Promise every 5 seconds for 30 seconds.
 * IMPORTANT: Do NOT polute this function with UI like notifications/toasts
 * ...this is intentially abstracted to be portable and reusable with any client app.
 * (You should be handling notifications/toasts/messaging within your component
 * try/catch, using the object that fetchWithPolling returns).
 * @param {Promise<T>} fetchPromise Promise<T>
 * @param {(response: T) => boolean} validatePromise (response: T) => boolean
 * @param {(attempt: number) => void} beforeAttempt (attempt: number) => void
 * @param {(attempt: number) => void} afterAttempt (attempt: number) => void
 * @param {number} pollInterval number
 * @param {number} pollTimeout number
 * @returns {Promise<FetchWithPollingResponse<T>>} Promise<FetchWithPollingResponse<T>>
 */
export const fetchWithPolling = <T>(
  /** The Promise that needs to be polled */
  fetchPromise: () => Promise<T>,
  /** A function to tell the polling if the Promise returned successfully */
  validatePromise: (response: T) => boolean,
  /** Defaults to polling every 5 seconds */
  pollInterval = 5 * 1000,
  /** Defaults to a 60 seconds max timeout */
  pollTimeout = 60 * 1000,
  /** A callback to run before each polling attempt */
  beforeAttempt?: (attempt: number) => void,
  /** A callback to run after each polling attempt */
  afterAttempt?: (attempt: number) => void
): Promise<FetchWithPollingResponse<T>> => {
  // these are intentionally initialized outside executePoll
  let attempt = 0;
  const endTime = new Date().getTime() + pollTimeout;

  // eslint-disable-next-line consistent-return
  const executePoll = async (resolve: Function, reject: Function): Promise<FetchWithPollingResponse<T> | undefined> => {
    let pollingResponse: FetchWithPollingResponse<T> = {
      error: false,
      success: false,
      timeout: false
    };

    try {
      // execute the beforeAttempt if there is one
      if (beforeAttempt) {
        beforeAttempt(attempt);
      }

      const response = await fetchPromise();
      const isValid = validatePromise(response);

      // stop polling and resolve
      if (isValid) {
        pollingResponse = { ...pollingResponse, polledData: response, success: true };
        return resolve(pollingResponse);
      }

      // stop polling if we're pass the pollTimeout
      const now = new Date().getTime();
      if (now > endTime) {
        pollingResponse = { ...pollingResponse, timeout: true };
        return reject(pollingResponse);
      }

      // execute the afterAttempt if there is one
      if (afterAttempt) {
        afterAttempt(attempt);
      }

      attempt += 1;

      // else we poll again
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      setTimeout(executePoll, pollInterval, resolve, reject);
    } catch (err) {
      // if the promise returns an error, stop polling
      pollingResponse = { ...pollingResponse, error: true };
      return reject(pollingResponse);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  return new Promise(executePoll);
};

const defaultPollingDownloadMessages: string[] = [
  // these strings are intentionally a little bit longer each time
  // so that the user will notice the text changing (in English, at least)
  bfTranslate(`Downloading...`),
  bfTranslate(`Still downloading...`),
  bfTranslate(`Still preparing the download...`),
  bfTranslate(`Hang tight! Still downloading...`),
  bfTranslate(`Still working on it, please wait...`),
  bfTranslate(`This is taking a while, keep waiting...`),
  bfTranslate(`We haven't given up, please keep waiting...`),
];

export const getPollingDownloadMessage = (attempt: number): string => (attempt > defaultPollingDownloadMessages.length - 1 ? defaultPollingDownloadMessages[defaultPollingDownloadMessages.length - 1] : defaultPollingDownloadMessages[attempt]);

// NOTE: always keep SnakeToCamelCase at the bottom of this file, otherwise debugging doesn't work right
export type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${Lowercase<T>}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

interface UseFetchOptions extends FetchJsonOptions {
  /** If every variable in the dependency list is truthy, data will refetch */
  deps?: DependencyList;
  /** Defaults to true, if false this overrides the dependency array so that deps doesn't control refetching data */
  fetchOnMount?: boolean;
}

interface FetchInitialState<T> {
  loading: boolean;
  error?: Error;
  response?: T;
}

export interface FetchState<T> extends FetchInitialState<T> {
  fetch: () => Promise<void>;
}

/**
 * A React hook to fetch JSON data
 * @example const deleteExample = useFetch<MyInterface>({ fetchOnMount: false, method: 'DELETE', url: `/api/example/${key}` });
 * @example const getExample = useFetch<MyInterface>({ url: `/api/example` });
 * @example const postExample = useFetch<MyInterface>({ fetchOnMount: false, method: 'POST', url: `/api/example` });
 * @example const putExample = useFetch<MyInterface>({ fetchOnMount: false, method: 'PUT', url: `/api/example/${key}` });
 * @param  {UseFetchOptions} options UseFetchOptions
 * @returns {FetchState<T>} FetchState<T>
 */
export const useFetch = <T extends unknown>(options: UseFetchOptions): FetchState<T> => {
  const { deps, fetchOnMount = true, url, ...otherOptions } = options;

  const abortControllerRef = useRef<AbortController | null>(null);
  const [fetchState, setFetchState] = useState<FetchInitialState<T>>({
    error: undefined,
    // only set loading to true initially when fetchOnMount is true
    loading: fetchOnMount,
    response: undefined
  });

  const fetchData = async (): Promise<void> => {
    abortControllerRef.current = new AbortController();

    try {
      setFetchState((prevState) => ({ ...prevState, loading: true }));

      const response = await fetchJson<T>({ ...otherOptions, signal: abortControllerRef.current.signal, url });
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setFetchState((prevState) => ({ ...prevState, loading: false, response }));
      }
    } catch (err) {
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        const error = err as Error;
        setFetchState((prevState) => ({ ...prevState, error, loading: false }));
      }
    }
  };

  const dependencyList: DependencyList = deps ? [...deps, url] : [url];
  const hasAllTruthyDependencies = deps && dependencyList.every((dep) => !!dep === true);

  useEffect(() => {
    if ((fetchOnMount || hasAllTruthyDependencies) && url) {
      fetchData();
    }

    return (): void => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchOnMount, hasAllTruthyDependencies, url]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...fetchState, fetch: fetchData } as FetchState<T>;
};
